import tokenStore from '../Utils/tokenStore';
import identityService from './identityService';

const APPLICATION_ID = process.env.REACT_APP_APPLICATION_ID || '';

const toBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return window.btoa(binary);
};

const fromBase64 = (value) => {
  if (!value) return new Uint8Array();
  let normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  while (normalized.length % 4) normalized += '=';
  const binary = window.atob(normalized);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

// Public shared hosting suffixes where claiming parent domain is rejected
const PUBLIC_SUFFIXES = ['azurewebsites.net', 'netlify.app', 'vercel.app', 'github.io', 'herokuapp.com'];

const getSharedRpId = () => {
  if (typeof window === 'undefined') return 'localhost';
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return host;
  const parts = host.split('.');
  const parentDomain = parts.length >= 2 ? parts.slice(-2).join('.') : host;
  if (PUBLIC_SUFFIXES.includes(parentDomain)) return host;
  return parentDomain;
};

export const isPasskeyDomainValid = () => true;

const getCurrentUserId = () => {
  const userGuid = tokenStore.getUserGuid();
  if (userGuid) return String(userGuid);
  const userId = tokenStore.getUserId();
  return userId ? String(userId) : null;
};

const getDeviceName = () => {
  if (typeof window === 'undefined') return 'Web Browser';
  const ua = window.navigator.userAgent || '';
  const m = ua.match(/(Chrome|CriOS|Firefox|FxiOS|Edg|Safari|OPR)\/[\d.]+/i);
  const browser = m
    ? m[1].replace('CriOS', 'Chrome').replace('FxiOS', 'Firefox').replace('Edg', 'Edge').replace('OPR', 'Opera')
    : 'Browser';
  if (/iPhone/.test(ua)) return `Safari • iPhone`;
  if (/iPad/.test(ua)) return `Safari • iPad`;
  if (/Android/.test(ua)) return `${browser} • Android`;
  if (/Macintosh/.test(ua)) return `${browser} • macOS`;
  if (/Windows/.test(ua)) return `${browser} • Windows`;
  return `${browser} • Desktop`;
};

const getCredentialLookup = (passkeys) => {
  const lookup = new Map();
  passkeys.forEach((passkey) => {
    if (!passkey?.credentialId) return;
    lookup.set(passkey.credentialId, passkey);
    const bytes = fromBase64(passkey.credentialId);
    lookup.set(toBase64(bytes), passkey);
  });
  return lookup;
};

const isSupported = () =>
  typeof window !== 'undefined' && !!window.PublicKeyCredential && !!window.navigator?.credentials;

const ensureSupported = async () => {
  if (!isSupported()) {
    throw new Error('Passkeys are not supported in this browser.');
  }
  if (typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function') {
    const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    if (!available) {
      throw new Error('A device passkey authenticator is not available.');
    }
  }
};

const passkeyService = {
  isSupported,

  authenticateWithEmail: async (email) => {
    await ensureSupported();

    const discovery = await identityService.discoverPasskeys({ email });
    if (!discovery?.availablePasskeys?.length) {
      throw new Error('No passkeys are registered for this account.');
    }

    const challenge = await identityService.getPasskeyChallenge({ email });
    const availablePasskeys = challenge?.availablePasskeys || discovery.availablePasskeys || [];
    if (!availablePasskeys.length) {
      throw new Error('No compatible passkeys are available for this account.');
    }

    const credential = await window.navigator.credentials.get({
      publicKey: {
        challenge: fromBase64(challenge.challenge),
        rpId: getSharedRpId(),
        timeout: challenge.timeout || 60000,
        userVerification: challenge.requireUserVerification ? 'required' : 'preferred',
        allowCredentials: availablePasskeys
          .filter((p) => p.credentialId)
          .map((p) => ({ type: 'public-key', id: fromBase64(p.credentialId) })),
      },
      mediation: 'optional',
    });

    if (!credential) throw new Error('Passkey authentication failed.');

    const passkeyLookup = getCredentialLookup(availablePasskeys);
    const credentialId = toBase64(credential.rawId);

    return identityService.authenticateWithPasskey({
      credentialId,
      signature: toBase64(credential.response.signature),
      authenticatorData: toBase64(credential.response.authenticatorData),
      clientDataJSON: toBase64(credential.response.clientDataJSON),
      challenge: challenge.challenge,
      email,
      applicationId: APPLICATION_ID,
      passkeyId: passkeyLookup.get(credentialId)?.id,
    });
  },

  registerCurrentUserPasskey: async () => {
    await ensureSupported();

    const userGuid = tokenStore.getUserGuid();
    const userId = getCurrentUserId();
    if (!userGuid && !userId) {
      throw new Error('You must be signed in to register a passkey.');
    }

    const profile = tokenStore.getProfile();

    let passkeyType = 'Biometrics';
    try {
      const types = await identityService.getAvailablePasskeyTypes();
      if (Array.isArray(types) && types.length > 0 && types[0]?.type) {
        passkeyType = types[0].type;
      }
    } catch {
      // non-fatal, fall back to Biometrics
    }

    const challenge = await identityService.getPasskeyChallenge({ userId: String(userGuid || userId) });
    const availablePasskeys = challenge?.availablePasskeys || [];
    const encoder = new window.TextEncoder();
    const userIdentifier = String(profile?.userFriendlyId || userId);

    const credential = await window.navigator.credentials.create({
      publicKey: {
        challenge: fromBase64(challenge.challenge),
        rp: {
          name: 'Vizybility Rental',
          id: getSharedRpId(),
        },
        user: {
          id: encoder.encode(userIdentifier),
          name: profile?.email || tokenStore.getEmail() || userIdentifier,
          displayName: profile?.fullName || tokenStore.getFullname() || profile?.email || userIdentifier,
        },
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },
          { type: 'public-key', alg: -257 },
        ],
        timeout: challenge.timeout || 60000,
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          residentKey: 'preferred',
          userVerification: challenge.requireUserVerification ? 'required' : 'preferred',
        },
        excludeCredentials: availablePasskeys
          .filter((p) => p.credentialId)
          .map((p) => ({ type: 'public-key', id: fromBase64(p.credentialId) })),
        attestation: 'none',
      },
    });

    if (!credential) throw new Error('Passkey registration was cancelled.');

    const attestationObject = credential.response.attestationObject;
    const publicKey =
      typeof credential.response.getPublicKey === 'function' && credential.response.getPublicKey()
        ? toBase64(credential.response.getPublicKey())
        : toBase64(attestationObject);

    return identityService.registerPasskey({
      deviceName: getDeviceName(),
      passkeyType,
      publicKey,
      credentialId: toBase64(credential.rawId),
      deviceInfo: typeof window === 'undefined' ? '' : window.navigator.userAgent,
    });
  },
};

export default passkeyService;
